import React, { useEffect, useRef } from 'react';
import { formatPhoneInput } from './utils';

function OtpInput({ value, onChange, onComplete, disabled }) {
  const inputs = useRef([]);
  const submittedRef = useRef(false);
  const v = value || '';

  useEffect(() => { inputs.current[0]?.focus(); }, []);

  useEffect(() => {
    if (v.length === 6 && !submittedRef.current) {
      submittedRef.current = true;
      onComplete?.();
    } else if (v.length < 6) {
      submittedRef.current = false;
    }
  }, [v, onComplete]);

  function handleChange(e, idx) {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return;
    if (raw.length > 1) {
      const distributed = (v.slice(0, idx) + raw).replace(/\D/g, '').slice(0, 6);
      onChange(distributed);
      inputs.current[Math.min(distributed.length, 5)]?.focus();
      return;
    }
    const padded = v.padEnd(idx, ' ').slice(0, idx) + raw + v.slice(idx + 1, 6);
    onChange(padded.replace(/\D/g, '').slice(0, 6));
    if (idx < 5) inputs.current[idx + 1]?.focus();
  }

  function handleKeyDown(e, idx) {
    if (e.key === 'Backspace') {
      if (v[idx]) {
        e.preventDefault();
        onChange(v.slice(0, idx) + v.slice(idx + 1));
      } else if (idx > 0) {
        e.preventDefault();
        onChange(v.slice(0, idx - 1) + v.slice(idx));
        inputs.current[idx - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      inputs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < 5) {
      e.preventDefault();
      inputs.current[idx + 1]?.focus();
    } else if (e.key === 'Enter' && v.length === 6) {
      onComplete?.();
    }
  }

  function handlePaste(e) {
    const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    inputs.current[Math.min(text.length, 5)]?.focus();
  }

  return (
    <div className="otp-input-row">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className="otp-input-box"
          value={v[i] || ''}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

function AuthForms({
  variant, // 'booking' (step 9.5) or 'standalone' (step 12)
  selectedCenter, authMode, authPhone, authOtp, authFirstName, authLastName,
  authEmail, authDob, authGender, authReferralCode, authHowHeard,
  authPassword, authConfirmPassword, authMarketingEmail, authMarketingSms,
  authLoginUserName, authLoading, authError, showPassword,
  onAuthPhoneChange, onAuthOtpChange, onAuthFirstNameChange, onAuthLastNameChange,
  onAuthEmailChange, onAuthDobChange, onAuthGenderChange, onAuthReferralCodeChange,
  onAuthHowHeardChange, onAuthPasswordChange, onAuthConfirmPasswordChange,
  onAuthMarketingEmailChange, onAuthMarketingSmsChange,
  onShowPasswordToggle, onSetAuthMode, onSetAuthError, onSetAuthLoginUserName,
  onPhoneSubmit, onMultipleAccountsSubmit, onOtpSubmit, onPasswordSubmit,
  onForgotPassword, onSignupSubmit, onSendOtp, onBackToBooking,
  resetCode, resetNewPassword, resetConfirmPassword, resetSuccess,
  onResetCodeChange, onResetNewPasswordChange, onResetConfirmPasswordChange,
  onResetPassword
}) {
  const isBooking = variant === 'booking';

  function getTitle() {
    switch (authMode) {
      case 'phone': return isBooking ? 'SIGN IN TO BOOK' : 'SIGN IN';
      case 'multipleAccounts': return 'MULTIPLE ACCOUNTS FOUND';
      case 'otp': return 'ENTER VERIFICATION CODE';
      case 'password': return 'LOG IN TO CONTINUE';
      case 'forgotSent': return 'CHECK YOUR EMAIL';
      default: return 'CREATE YOUR ACCOUNT';
    }
  }

  function handlePhoneInputChange(e) {
    const val = e.target.value;
    if (/^[\d(]/.test(val) && !val.includes('@')) {
      onAuthPhoneChange(formatPhoneInput(val));
    } else {
      onAuthPhoneChange(val);
    }
  }

  return (
    <div className="step-content">
      <h1 className="step-title">{getTitle()}</h1>
      {isBooking && <p className="step-subtitle">{selectedCenter?.name}</p>}

      {isBooking ? <div className="auth-container">{renderAuthContent()}</div> : renderAuthContent()}

      {!isBooking && (
        <button className="auth-link" onClick={onBackToBooking} style={{ marginTop: '20px' }}>← Back to Booking</button>
      )}
    </div>
  );

  function renderAuthContent() {
    return (
      <>
        {authMode === 'phone' && (
          <div className="auth-form">
            <p className="auth-instruction">
              {isBooking ? 'Enter your phone number or email to sign in or create an account.' : 'Enter your phone number or email to sign in.'}
            </p>
            <div className="auth-field">
              <label className="auth-label">{isBooking ? 'PHONE NUMBER OR EMAIL' : 'PHONE OR EMAIL'}</label>
              <input
                type="text"
                className="auth-input"
                placeholder={isBooking ? '(778) 555-1234 or email@example.com' : '(604) 555-1234 or email@example.com'}
                autoComplete="username"
                name="username"
                value={authPhone}
                onChange={handlePhoneInputChange}
                onKeyDown={(e) => e.key === 'Enter' && onPhoneSubmit(authPhone.includes('@') ? 'password' : 'otp')}
                autoFocus
              />
            </div>
            {authError && <p className="auth-error">{authError}</p>}
            {onSendOtp && !authPhone.includes('@') ? (
              <>
                <p className="auth-hint">You'll receive a 6-digit verification code.</p>
                <button className="auth-button" onClick={() => onPhoneSubmit('otp')} disabled={authLoading}>
                  {authLoading ? 'CHECKING...' : 'SEND VERIFICATION CODE'}
                </button>
                <div className="auth-divider"><span>Or continue with</span></div>
                <button className="auth-alt-button" onClick={() => onPhoneSubmit('password')} disabled={authLoading}>
                  CONTINUE WITH PASSWORD
                </button>
              </>
            ) : (
              <button className="auth-button" onClick={() => onPhoneSubmit('password')} disabled={authLoading}>
                {authLoading ? 'CHECKING...' : 'CONTINUE'}
              </button>
            )}
          </div>
        )}

        {authMode === 'multipleAccounts' && (
          <div className="auth-form">
            <p className="auth-instruction">
              We found a couple of accounts under that number. What's your first name?
            </p>
            <div className="auth-field">
              <label className="auth-label">FIRST NAME</label>
              <input
                type="text"
                className="auth-input"
                placeholder={isBooking ? 'First name' : 'Your first name'}
                value={authFirstName}
                onChange={(e) => onAuthFirstNameChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onMultipleAccountsSubmit()}
                autoFocus
              />
            </div>
            {authError && <p className="auth-error">{authError}</p>}
            <button className="auth-button" onClick={onMultipleAccountsSubmit} disabled={authLoading}>
              {authLoading ? (isBooking ? 'LOOKING YOU UP...' : 'LOOKING UP...') : (isBooking ? 'CONFIRM' : 'CONTINUE')}
            </button>
            <div className="auth-divider"><span>Or continue with</span></div>
            <button className="auth-alt-button" onClick={() => { onSetAuthMode('password'); onSetAuthError(''); }} disabled={authLoading}>
              Password
            </button>
            <button className="auth-link" onClick={() => { onSetAuthMode('phone'); onSetAuthError(''); onAuthFirstNameChange(''); }}>
              Use a different number
            </button>
          </div>
        )}

        {authMode === 'otp' && (
          <div className="auth-form">
            <p className="auth-instruction">We sent a verification code to <strong>{authPhone}</strong></p>
            <div className="auth-field">
              <label className="auth-label">VERIFICATION CODE</label>
              <OtpInput
                value={authOtp}
                onChange={(v) => onAuthOtpChange(v)}
                onComplete={onOtpSubmit}
                disabled={authLoading}
              />
            </div>
            {authError && <p className="auth-error">{authError}</p>}
            <button className="auth-button" onClick={onOtpSubmit} disabled={authLoading}>
              {authLoading ? 'VERIFYING...' : 'VERIFY & CONTINUE'}
            </button>
            {onSendOtp && (
              <button className="auth-link" onClick={() => { onSetAuthError(''); onSendOtp(); }} disabled={authLoading}>
                Resend code
              </button>
            )}
            <div className="auth-divider"><span>Or continue with</span></div>
            <button className="auth-alt-button" onClick={() => { onSetAuthMode('password'); onSetAuthError(''); onAuthPasswordChange(''); }}>
              Password
            </button>
            <button className="auth-link" onClick={() => { onSetAuthMode('phone'); onAuthOtpChange(''); onSetAuthError(''); }}>
              Use a different number
            </button>
          </div>
        )}

        {authMode === 'password' && (
          <div className="auth-form">
            {authLoginUserName ? (
              <>
                <p className="auth-instruction">Welcome back{authFirstName ? `, ${authFirstName}` : ''}! Enter your password to continue.</p>
                <div className="auth-field">
                  <label className="auth-label">PASSWORD</label>
                  <div className="auth-password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="auth-input"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      name="password"
                      value={authPassword}
                      onChange={(e) => onAuthPasswordChange(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && onPasswordSubmit()}
                      autoFocus
                    />
                    <button type="button" className="auth-show-password" onClick={onShowPasswordToggle}>
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="auth-instruction">Welcome back! Sign in with your email or phone number.</p>
                <div className="auth-field">
                  <label className="auth-label">EMAIL OR PHONE</label>
                  <input
                    type="text"
                    className="auth-input"
                    placeholder="Email or phone number"
                    autoComplete="username"
                    name="username"
                    value={authPhone}
                    onChange={(e) => { onAuthPhoneChange(e.target.value); onSetAuthLoginUserName(''); }}
                    autoFocus
                  />
                </div>
                <div className="auth-field">
                  <label className="auth-label">PASSWORD</label>
                  <div className="auth-password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="auth-input"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      name="password"
                      value={authPassword}
                      onChange={(e) => onAuthPasswordChange(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && onPasswordSubmit()}
                    />
                    <button type="button" className="auth-show-password" onClick={onShowPasswordToggle}>
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
              </>
            )}
            {authError && <p className="auth-error">{authError}</p>}
            <button className="auth-button" onClick={onPasswordSubmit} disabled={authLoading}>
              {authLoading ? 'SIGNING IN...' : 'LOG IN'}
            </button>
            <button className="auth-link" onClick={onForgotPassword} disabled={authLoading}>
              Forgot your password?
            </button>
            {onSendOtp && (
              <button className="auth-link" onClick={onSendOtp} disabled={authLoading}>
                Use verification code instead
              </button>
            )}
            <button className="auth-link" onClick={() => { onSetAuthMode('phone'); onSetAuthError(''); onAuthPasswordChange(''); }}>
              Use a different number
            </button>
            <div className="auth-divider"><span>Don't have an account?</span></div>
            <button className="auth-alt-button" onClick={() => { onSetAuthMode('signup'); onSetAuthError(''); }}>
              Create a new account
            </button>
          </div>
        )}

        {authMode === 'forgotSent' && (
          <div className="auth-form">
            {resetSuccess ? (
              <>
                <p className="auth-instruction">Your password has been reset successfully!</p>
                <button className="auth-button" onClick={() => { onSetAuthMode('password'); onSetAuthError(''); onAuthPasswordChange(''); }}>
                  LOG IN WITH NEW PASSWORD
                </button>
              </>
            ) : (
              <>
                <p className="auth-instruction">We've sent a reset code to your email. Enter it below with your new password.</p>
                <div className="auth-field">
                  <label className="auth-label">RESET CODE</label>
                  <input type="text" className="auth-input" placeholder="Enter code from email"
                    value={resetCode} onChange={(e) => onResetCodeChange(e.target.value)} autoFocus />
                </div>
                <div className="auth-field">
                  <label className="auth-label">NEW PASSWORD</label>
                  <div className="auth-password-wrapper">
                    <input type={showPassword ? 'text' : 'password'} className="auth-input" placeholder="New password"
                      autoComplete="new-password" value={resetNewPassword}
                      onChange={(e) => onResetNewPasswordChange(e.target.value)} />
                    <button type="button" className="auth-show-password" onClick={onShowPasswordToggle}>
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>
                <div className="auth-field">
                  <label className="auth-label">CONFIRM PASSWORD</label>
                  <input type="password" className="auth-input" placeholder="Confirm new password"
                    autoComplete="new-password" value={resetConfirmPassword}
                    onChange={(e) => onResetConfirmPasswordChange(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onResetPassword()} />
                </div>
                <div className="auth-password-rules">
                  <p className="auth-password-title">Password must contain:</p>
                  <span className={resetNewPassword && resetNewPassword.length >= 8 ? 'pass' : ''}>✓ 8+ characters</span>
                  <span className={resetNewPassword && /[a-z]/.test(resetNewPassword) ? 'pass' : ''}>✓ Lowercase [a-z]</span>
                  <span className={resetNewPassword && /[A-Z]/.test(resetNewPassword) ? 'pass' : ''}>✓ Uppercase [A-Z]</span>
                  <span className={resetNewPassword && /[0-9]/.test(resetNewPassword) ? 'pass' : ''}>✓ Number [0-9]</span>
                  <span className={resetNewPassword && /[~!@#$%^&*()_+=]/.test(resetNewPassword) ? 'pass' : ''}>✓ Special character</span>
                </div>
                {authError && <p className="auth-error">{authError}</p>}
                <button className="auth-button" onClick={onResetPassword} disabled={authLoading}>
                  {authLoading ? 'RESETTING...' : 'RESET PASSWORD'}
                </button>
                <button className="auth-link" onClick={onForgotPassword} disabled={authLoading}>
                  Resend code
                </button>
                <button className="auth-link" onClick={() => { onSetAuthMode('password'); onSetAuthError(''); }}>
                  Back to log in
                </button>
              </>
            )}
          </div>
        )}

        {authMode === 'signup' && (
          <div className="auth-form">
            <p className="auth-instruction">
              {isBooking
                ? <>We don't have an account for <strong>{authPhone}</strong>. Let's create one!</>
                : "Let's create your account!"}
            </p>
            <div className="auth-row">
              <div className="auth-field">
                <label className="auth-label">FIRST NAME *</label>
                <input type="text" className="auth-input" placeholder="First name"
                  value={authFirstName} onChange={(e) => onAuthFirstNameChange(e.target.value)} autoFocus />
              </div>
              <div className="auth-field">
                <label className="auth-label">LAST NAME *</label>
                <input type="text" className="auth-input" placeholder="Last name"
                  value={authLastName} onChange={(e) => onAuthLastNameChange(e.target.value)} />
              </div>
            </div>

            <div className="auth-row">
              <div className="auth-field">
                <label className="auth-label">DATE OF BIRTH *</label>
                <input type="date" className="auth-input"
                  value={authDob} onChange={(e) => onAuthDobChange(e.target.value)} />
              </div>
              <div className="auth-field">
                <label className="auth-label">GENDER (OPTIONAL)</label>
                <select className="auth-input" value={authGender} onChange={(e) => onAuthGenderChange(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  <option value="1">Female</option>
                  <option value="0">Male</option>
                  <option value="2">Non-binary</option>
                </select>
              </div>
            </div>

            <div className="auth-row">
              <div className="auth-field">
                <label className="auth-label">EMAIL *</label>
                <input type="email" className="auth-input" placeholder="email@example.com"
                  autoComplete="email" name="email"
                  value={authEmail} onChange={(e) => onAuthEmailChange(e.target.value)} />
              </div>
              <div className="auth-field">
                <label className="auth-label">PHONE{isBooking ? '' : ' *'}</label>
                <input type="text" className="auth-input" value={authPhone}
                  onChange={(e) => onAuthPhoneChange(formatPhoneInput(e.target.value))}
                  autoComplete="tel" name="tel"
                  placeholder="(778) 555-1234"
                  readOnly={!isBooking} />
              </div>
            </div>

            <div className="auth-row">
              <div className="auth-field">
                <label className="auth-label">REFERRAL CODE</label>
                <input type="text" className="auth-input" placeholder="Optional"
                  value={authReferralCode} onChange={(e) => onAuthReferralCodeChange(e.target.value)} />
              </div>
              <div className="auth-field">
                <label className="auth-label">HOW DID YOU HEAR ABOUT US?</label>
                <select className="auth-input" value={authHowHeard} onChange={(e) => onAuthHowHeardChange(e.target.value)}>
                  <option value="">Select...</option>
                  <option value="Advertisement">Advertisement</option>
                  <option value="Chat Gpt">Chat Gpt</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Friend Referral">Friend Referral</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Internet Search">Internet Search</option>
                  <option value="Other">Other</option>
                  <option value="Walk-in">Walk-in</option>
                  <option value="Word of Mouth">Word of Mouth</option>
                </select>
              </div>
            </div>

            <div className="auth-row">
              <div className="auth-field">
                <label className="auth-label">PASSWORD *</label>
                <input type="password" className="auth-input" placeholder="Password"
                  autoComplete="new-password" name="new-password"
                  value={authPassword} onChange={(e) => onAuthPasswordChange(e.target.value)} />
              </div>
              <div className="auth-field">
                <label className="auth-label">CONFIRM PASSWORD *</label>
                <input type="password" className="auth-input" placeholder="Confirm password"
                  autoComplete="new-password" name="confirm-password"
                  value={authConfirmPassword} onChange={(e) => onAuthConfirmPasswordChange(e.target.value)} />
              </div>
            </div>

            <div className="auth-password-rules">
              <p className="auth-password-title">Password must contain:</p>
              <span className={authPassword.length >= 8 ? 'pass' : ''}>✓ 8+ characters</span>
              <span className={/[a-z]/.test(authPassword) ? 'pass' : ''}>✓ Lowercase [a-z]</span>
              <span className={/[A-Z]/.test(authPassword) ? 'pass' : ''}>✓ Uppercase [A-Z]</span>
              <span className={/[0-9]/.test(authPassword) ? 'pass' : ''}>✓ Number [0-9]</span>
              <span className={/[~!@#$%^&*()_+=]/.test(authPassword) ? 'pass' : ''}>✓ Special character</span>
            </div>
            <div className="auth-marketing">
              <p className="auth-marketing-title">Marketing Opt-in Consent</p>
              <label className="auth-checkbox-label">
                <input type="checkbox" checked={authMarketingEmail} onChange={(e) => onAuthMarketingEmailChange(e.target.checked)} />
                Email
              </label>
              <label className="auth-checkbox-label">
                <input type="checkbox" checked={authMarketingSms} onChange={(e) => onAuthMarketingSmsChange(e.target.checked)} />
                SMS
              </label>
            </div>

            {authError && <p className="auth-error">{authError}</p>}
            <button className="auth-button" onClick={onSignupSubmit} disabled={authLoading}>
              {authLoading ? 'CREATING ACCOUNT...' : (isBooking ? 'CREATE ACCOUNT & BOOK' : 'CREATE ACCOUNT')}
            </button>
            <button className="auth-link" onClick={() => { onSetAuthMode('phone'); onSetAuthError(''); }}>
              {isBooking ? 'Use a different number' : 'Back'}
            </button>
          </div>
        )}
      </>
    );
  }
}

export default AuthForms;
